/**
 * Softens the edge of a solid mask: the alpha of a box blur of the mask, padded by the radius on every side so the blur is not cut
 * at the mask's own edge. Used for the place a roof keeps dry, so the soak fades over a few pixels at the building's outline.
 *
 * @param solid One byte a pixel, 1 where solid, row-major, `width` by `height`.
 * @param width The mask's width.
 * @param height The mask's height.
 * @param radius The blur's radius in pixels, 0 or more.
 * @returns Alpha, one byte a pixel, `width + 2 * radius` by `height + 2 * radius`, row-major.
 */
export function featherAlpha(solid: Uint8Array, width: number, height: number, radius: number): Uint8ClampedArray {
  const w = width + 2 * radius;
  const h = height + 2 * radius;
  const wide = new Float32Array(w * h);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (solid[y * width + x] === 1) wide[(y + radius) * w + x + radius] = 1;
    }
  }
  const span = 2 * radius + 1;
  const across = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    let sum = 0;
    for (let x = -radius; x <= radius; x += 1) sum += x >= 0 && x < w ? (wide[y * w + x] as number) : 0;
    for (let x = 0; x < w; x += 1) {
      across[y * w + x] = sum / span;
      const out = x - radius;
      const into = x + radius + 1;
      if (out >= 0) sum -= wide[y * w + out] as number;
      if (into < w) sum += wide[y * w + into] as number;
    }
  }
  const out = new Uint8ClampedArray(w * h);
  for (let x = 0; x < w; x += 1) {
    let sum = 0;
    for (let y = -radius; y <= radius; y += 1) sum += y >= 0 && y < h ? (across[y * w + x] as number) : 0;
    for (let y = 0; y < h; y += 1) {
      out[y * w + x] = Math.round((sum / span) * 255);
      const gone = y - radius;
      const into = y + radius + 1;
      if (gone >= 0) sum -= across[gone * w + x] as number;
      if (into < h) sum += across[into * w + x] as number;
    }
  }
  return out;
}
